import {
  Box,
  Chip,
  FormHelperText,
  MenuItem,
  OutlinedInput,
  Select,
  SxProps,
  Theme,
} from '@mui/material';
import { ControllerRenderProps } from 'react-hook-form';
import ArrowDownIcon from '../../../../../components/Icons/ArrowDownIcon';
import { IJopPostInputs } from '../../../../../interfaces/jobpost.interface';
import { ISelectItems } from './JobPostFilterMultiSelect';
import { hasProperty } from './helper/utility';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
    sx: {
      '& .MuiMenu-list': {
        paddingTop: '0px',
        paddingBottom: '0px',
        maxHeight: '200px',
        overflowY: 'auto',
      },
    },
  },
};

interface FlexibleMultiSelectProps {
  label: string;
  field?: ControllerRenderProps<IJopPostInputs, keyof IJopPostInputs>;
  selectItems: ISelectItems;
  placeholder: string;
  value?: string[];
  onChange?: (value: string[]) => void;
  helperText?: string;
}

const FlexibleMultiSelect = ({
  field,
  placeholder,
  selectItems,
  label,
  value,
  onChange,
  helperText,
}: FlexibleMultiSelectProps) => {
  const {
    onChange: onFieldValueChange,
    value: fieldValue,
    ...restFieldProps
  } = field || {};

  // Always provide a defined value array to keep Select controlled
  const selectValue: string[] = Array.isArray(fieldValue)
    ? (fieldValue as string[])
    : Array.isArray(value)
      ? value
      : [];

  return (
    <>
      <Select
        {...restFieldProps}
        value={selectValue}
        multiple={true}
        displayEmpty
        onChange={(event) => {
          const { value } = event.target;
          onFieldValueChange?.(value);

          if (
            Array.isArray(value) &&
            value.every((item) => typeof item === 'string')
          ) {
            onChange?.(value);
          }
        }}
        IconComponent={(props) => (
          <ArrowDownIcon
            {...props}
            sx={{
              mr: '15px',
              mt: '4px',
              backgroundColor: 'transparent !important',
            }}
          />
        )}
        sx={selectSx}
        input={<OutlinedInput />}
        renderValue={(selected) => {
          if (!Array.isArray(selected) || !selected.length) {
            return placeholder;
          }
          return (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                maxHeight: '104px',
                overflowY: 'auto',
              }}
            >
              {selected.map((value) => {
                const industry = selectItems.find((item) => item.id === value);
                if (industry && hasProperty(industry, label)) {
                  return (
                    <Chip
                      key={value}
                      label={industry.industry}
                      onMouseDown={(event) => event.stopPropagation()}
                      onDelete={(event) => {
                        event.stopPropagation();
                        const updatedItems = selected.filter(
                          (item) => item !== value
                        );
                        field?.onChange(updatedItems);
                        onChange?.(updatedItems);
                      }}
                    />
                  );
                }
                return null;
              })}
            </Box>
          );
        }}
        MenuProps={MenuProps}
        inputProps={{ 'aria-label': 'Without label' }}
      >
        {selectItems.map((item) => (
          <MenuItem
            key={item.id}
            value={item.id}
            sx={{ fontSize: '14px', fontWeight: 400 }}
          >
            {item.industry}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText id="helper-text" sx={{ color: 'primary.main' }}>
        {helperText}
      </FormHelperText>
    </>
  );
};

export default FlexibleMultiSelect;

const selectSx: SxProps<Theme> = (theme) => ({
  '& .MuiSelect-select.MuiSelect-outlined.MuiSelect-multiple.MuiInputBase-input.MuiOutlinedInput-input':
    {
      display: 'flex',
      alignItems: 'center',
      height: 'fit-content',
      minHeight: '50px',
      p: '9px 3px 9px 11px',
      borderRadius: 0,
    },
  '& .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input':
    {
      alignItems: 'center',
      height: 'fit-content',
      p: '12px 25px 12px 14px',
    },
  '& .MuiSelect-select:focus': {
    outline: 'none',
  },
  '& .MuiBox-root': {
    paddingRight: '40px',
  },
  '& .MuiSelect-select:focusWithin': {
    outline: 'none',
  },
  '& .MuiSelect-icon': {
    color: theme.palette.text.primary,
    backgroundColor: 'transparent',
  },
  boxShadow: 'none',
  '.MuiOutlinedInput-notchedOutline': { border: 0 },
  '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    border: 0,
  },
  '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 0,
  },
});
